import React, { SyntheticEvent, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import ErrorDisplay from 'components/ErrorDisplay';
import { DomainItem } from 'models/Domain';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { RootState, getDomainSlice } from 'store';
import Snackbars from 'components/Alerts';
import DeleteConfirmationModal from 'components/DeleteConfirmationModal/DeleteConfirmationModal';
import { CrudViewListProps } from '../utils';

import useStyles from '../styles';

function ListView({
  domain,
  children,
  filter,
  sortBy,
  autoLoad = true,
  paginationOptions = { pageIndex: 0, pageSize: 25 },
  dispatchProps,
  loadingDependency = false,
  disableErrorDisplay = false,
  className,
  deleteCallback
}: CrudViewListProps) {
  const classes = useStyles();
  const history = useHistory();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();

  const { selector, actions } = getDomainSlice(domain);

  const { items: data, isLoading, error } = useSelector<
    RootState,
    ReturnType<typeof selector>
  >(selector);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [internalDomainItem, setDomainItem] = useState<
    DomainItem | undefined
  >();

  const onEdit = (domainItem: DomainItem, overrideUrl?: string) => (
    event: SyntheticEvent<any>
  ) => {
    event.stopPropagation();
    history.push(`${overrideUrl || url}/${domainItem.id}/edit`, domainItem);
  };

  const onView = (domainItem: DomainItem) => () => {
    history.push(`${url}/${domainItem.id}`, domainItem);
  };

  const onDelete = (domainItem: DomainItem) => (event: SyntheticEvent<any>) => {
    event.stopPropagation();

    setDomainItem(domainItem);
    setIsDeleteModalOpen(true);
  };
  const closeModal = () => {
    setIsDeleteModalOpen(false);
    setDomainItem(undefined);
  };

  const onDeleteConfirm = () => {
    if (internalDomainItem) {
      dispatch(actions.delete(internalDomainItem, deleteCallback));
      closeModal();
    }
  };

  const defaultDispatchProps = [filter, paginationOptions, undefined, sortBy];

  useEffect(() => {
    if (autoLoad) {
      dispatch(actions.get.list(...(dispatchProps || defaultDispatchProps)));
    }
  }, [autoLoad]);

  if (children == null) {
    return null;
  }
  return (
    <>
      <Grid
        container
        className={`${classes.listContainer} ${className || ''}`}
        data-qa-id="scrollIntoView"
        data-testid="listViewContainer"
      >
        <Grid item xs={12}>
          {disableErrorDisplay || !error
            ? null
            : error && <ErrorDisplay status={error.status} />}
          {(isLoading || loadingDependency) && (
            <div className={classes.loadingWrapper}>
              <CircularProgress
                size={48}
                className={classes.spinnerWrapper}
                data-qa-id="readDomainSpinner"
                data-testid="readDomainSpinner"
              />
            </div>
          )}
          {data?.length >= 0 &&
            !isLoading &&
            !loadingDependency &&
            children({
              onEdit,
              onDelete,
              onView
            })}
        </Grid>
      </Grid>
      <Snackbars />
      <DeleteConfirmationModal
        onDelete={onDeleteConfirm}
        onCancel={closeModal}
        onClose={closeModal}
        open={isDeleteModalOpen}
        title={`Delete ${domain.singular}`}
        deletionSubject={domain.singular}
        testId="listPageDeletionModal"
      />
    </>
  );
}

export default ListView;
